import React, { useState, useEffect, useRef } from 'react';
import { Users, Camera, Zap, TrendingUp, DollarSign, AlertCircle, Download, Activity, CheckCircle, XCircle, BarChart3, FileDown, ImageDown, Eye } from 'lucide-react';
import { API_URL } from '../../config';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart 
} from 'recharts';

export default function AdminDashboard({ adminData, onViewChange }) {
  const [kpis, setKpis] = useState(null);
  const [health, setHealth] = useState(null);
  const [chartsData, setChartsData] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(null);  // NEU: Online User
  const [activeUsersDetailed, setActiveUsersDetailed] = useState(null);  // NEU: Detailierte User-Liste
  const [detailPeriod, setDetailPeriod] = useState('today');  // NEU: Period für Detail-Ansicht
  const [bannedIPs, setBannedIPs] = useState(null);  // Blocked IPs Liste
  const [banningIP, setBanningIP] = useState(null);  // IP die gerade gebannt wird
  const [guestStats, setGuestStats] = useState(null);  // Guest Traffic Analytics
  const [guestVisitors, setGuestVisitors] = useState(null);  // Individual guest visits
  const [guestPeriod, setGuestPeriod] = useState('today');  // Period for guest detail view
  const [influencerName, setInfluencerName] = useState('');  // Link Generator
  const [generatedLink, setGeneratedLink] = useState('');  // Generated tracking link
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const chartsRef = useRef(null);

  useEffect(() => {
    fetchKPIs();
    fetchHealth();
    fetchCharts();
    fetchOnlineUsers();  // NEU: Online User laden
    fetchActiveUsersDetailed();  // NEU: Detailierte User laden
    fetchBannedIPs();  // Blocked IPs laden
    fetchGuestStats();  // Guest Traffic Analytics
    fetchGuestVisitors();  // Individual guest visits

    // Auto-refresh alle 30s für User Stats
    const interval = setInterval(() => {
      fetchOnlineUsers();
      // Nur refreshen wenn "Live" Tab aktiv ist
      if (detailPeriod === 'now') {
        fetchActiveUsersDetailed('now');
      }
    }, 30000);
    
    // LIVE Guest Stats - alle 30 Sekunden
    const guestInterval = setInterval(() => {
      fetchGuestStats();
      fetchGuestVisitors();
    }, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(guestInterval);
    };
  }, [period, detailPeriod]);

  // Detail-Period Änderung
  useEffect(() => {
    fetchActiveUsersDetailed(detailPeriod);
  }, [detailPeriod]);

  // Guest-Period Änderung
  useEffect(() => {
    fetchGuestVisitors(guestPeriod);
  }, [guestPeriod]);


  const fetchOnlineUsers = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/admin/online-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setOnlineUsers(data);
      }
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  const fetchActiveUsersDetailed = async (p = detailPeriod) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/admin/active-users-detailed?period=${p}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setActiveUsersDetailed(data);
      }
    } catch (error) {
      console.error('Error fetching active users detailed:', error);
    }
  };

  const fetchBannedIPs = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/admin/banned-ips`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setBannedIPs(data);
      }
    } catch (error) {
      console.error('Error fetching banned IPs:', error);
    }
  };

  const fetchGuestStats = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/admin/guest-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setGuestStats(data);
      }
    } catch (error) {
      console.error('Error fetching guest stats:', error);
    }
  };

  const fetchGuestVisitors = async (p = guestPeriod) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;

      const res = await fetch(`${API_URL}/api/admin/guest-visits-recent?period=${p}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setGuestVisitors(data);
      }
    } catch (error) {
      console.error('Error fetching guest visitors:', error);
    }
  };

  const banIP = async (ip, reason = 'Manual ban') => {
    if (!ip || ip === '-') return;
    setBanningIP(ip);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/ban-ip`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip, reason })
      });
      
      if (res.ok) {
        alert(`IP ${ip} wurde gesperrt!`);
        fetchBannedIPs();
      } else {
        const err = await res.json();
        alert(`Fehler: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error banning IP:', error);
      alert('Fehler beim Sperren der IP');
    } finally {
      setBanningIP(null);
    }
  };

  const unbanIP = async (ip) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/unban-ip?ip=${encodeURIComponent(ip)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        alert(`IP ${ip} wurde entsperrt!`);
        fetchBannedIPs();
      }
    } catch (error) {
      console.error('Error unbanning IP:', error);
    }
  };

  const fetchKPIs = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      
      // Check if token exists (iOS Safari Private Mode issue)
      if (!token) {
        console.error('❌ No snapshot token found - user not logged in');
        setLoading(false);
        return;
      }
      
      // Token nicht loggen (Sicherheitsrisiko)
      
      const res = await fetch(`${API_URL}/api/admin/dashboard/kpis?period=${period}`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include' // iOS Safari cookie support
      });
      
      console.log('📡 KPI Response Status:', res.status);
      
      if (res.status === 401 || res.status === 403) {
        console.error('❌ Unauthorized - redirecting to login');
        localStorage.removeItem('snapshot_token');
        window.location.href = '/';
        return;
      }
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      console.log('📊 KPIs Response:', data);
      console.log('   Revenue:', data.revenue);
      console.log('   Margin:', data.margin);
      console.log('   Users:', data.users);
      console.log('   Avatars:', data.avatars);
      
      setKpis(data);
    } catch (error) {
      console.error('❌ Error fetching KPIs:', error);
      // Don't show 0 on error - keep previous state or show error
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch health');
      
      const data = await res.json();
      setHealth(data);
    } catch (error) {
      console.error('Error fetching health:', error);
    }
  };

  const fetchCharts = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/charts/data?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch charts');
      
      const data = await res.json();
      setChartsData(data);
    } catch (error) {
      console.error('Error fetching charts:', error);
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem('snapshot_token');
    window.open(`${API_URL}/api/admin/export/transactions?period=${period}&format=csv`, '_blank');
  };

  const handleExportChartsCSV = () => {
    if (!chartsData || !chartsData.revenue_vs_costs) return;
    
    const csvContent = [
      ['Datum', 'Umsatz (€)', 'Astria-Kosten (€)', 'Käufe', 'Generierungen'],
      ...chartsData.revenue_vs_costs.map((item, idx) => [
        item.date,
        item.revenue.toFixed(2),
        item.astria_costs.toFixed(2),
        chartsData.purchases_per_day[idx]?.purchases || 0,
        chartsData.generations_per_day[idx]?.generations || 0
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `charts_data_${period}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleExportChartsPNG = async () => {
    if (!chartsRef.current) return;
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartsRef.current, {
        backgroundColor: '#000000',
        scale: 2
      });
      
      canvas.toBlob((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `charts_${period}_${new Date().toISOString().split('T')[0]}.png`;
        a.click();
      });
    } catch (error) {
      console.error('PNG export failed:', error);
      alert('PNG Export fehlgeschlagen. Bitte html2canvas installieren: yarn add html2canvas');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black grain-texture text-white p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="mb-4">
            <h1 className="text-2xl md:text-4xl font-bold mb-2">
              Admin <span className="text-[#10B981]">Dashboard</span>
            </h1>
            <p className="text-sm md:text-base text-white/60 break-words">
              Angemeldet als: <span className="text-[#10B981] font-medium">{adminData.email}</span>
              <span className="hidden sm:inline"> · Rolle: <span className="font-medium">{adminData.role}</span></span>
            </p>
          </div>

          {/* Time Filter + Export - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-black border-2 border-white/30 rounded-xl text-white focus:border-[#10B981] transition-all text-base sm:text-sm"
            >
              <option value="today">Heute</option>
              <option value="7d">7 Tage</option>
              <option value="30d">30 Tage</option>
              <option value="month">Monat</option>
              <option value="year">Jahr</option>
            </select>
            
            <button
              onClick={handleExport}
              className="w-full sm:w-auto px-6 py-3 sm:px-4 sm:py-2 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all font-medium flex items-center justify-center gap-2 text-base sm:text-sm"
            >
              <Download className="w-5 h-5 sm:w-4 sm:h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* OpenAI Quota Warning Banner */}
        {kpis?.openai_quota_warning?.active && (
          <div className="mb-6 p-4 bg-red-900/40 border-2 border-red-500/60 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-300">
                OpenAI API Quota-Warnung ({kpis.openai_quota_warning.count} Fehler in 24h)
              </p>
              <p className="text-xs text-red-300/70 mt-1">
                Quelle: {kpis.openai_quota_warning.latest_source} &middot; {kpis.openai_quota_warning.latest_at ? new Date(kpis.openai_quota_warning.latest_at).toLocaleString('de-DE') : ''}
              </p>
              <p className="text-xs text-red-300/50 mt-0.5 break-all">
                {kpis.openai_quota_warning.latest_message}
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('snapshot_token');
                  const res = await fetch(`${API_URL}/api/admin/openai-alerts/resolve`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                  });
                  if (res.ok) {
                    setKpis(prev => ({ ...prev, openai_quota_warning: null }));
                  }
                } catch (err) {
                  console.error('Failed to resolve alerts:', err);
                }
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-all flex-shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Business-Übersicht Grid - Mobile First, alle wichtigen Zahlen */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          
          {/* Block 1: Online User (Erweiterte Statistiken) */}
          <div className="p-4 md:p-6 bg-black border-2 border-[#10B981]/50 rounded-xl relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <div className="w-3 h-3 bg-[#10B981] rounded-full animate-pulse"></div>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Online User Statistik</h3>
            </div>
            
            {/* Hauptzahl: Jetzt online */}
            <p className="text-3xl md:text-4xl font-bold text-[#10B981] mb-1">
              {onlineUsers?.online_now || 0}
            </p>
            <p className="text-xs text-white/50 mb-4">
              🟢 Jetzt online (letzte 5 Min)
            </p>
            
            {/* Historische Statistiken */}
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/10">
              <div>
                <p className="text-xs text-white/50">Heute</p>
                <p className="text-base md:text-lg font-bold text-white">{onlineUsers?.online_today || 0}</p>
              </div>
              <div>
                <p className="text-xs text-white/50">7 Tage</p>
                <p className="text-base md:text-lg font-bold text-white">{onlineUsers?.online_7d || 0}</p>
              </div>
              <div>
                <p className="text-xs text-white/50">30 Tage</p>
                <p className="text-base md:text-lg font-bold text-white">{onlineUsers?.online_30d || 0}</p>
              </div>
              <div>
                <p className="text-xs text-white/50">Ganzes Jahr</p>
                <p className="text-base md:text-lg font-bold text-white">{onlineUsers?.online_year || 0}</p>
              </div>
            </div>
          </div>

          {/* Block 1a: Guest Traffic Analytics */}
          <div className="p-4 md:p-6 bg-black border-2 border-orange-500/30 rounded-xl relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 md:w-6 md:h-6 text-orange-400" />
                <h3 className="text-sm md:text-base font-bold text-white/80">Guest Traffic (ohne Login)</h3>
              </div>
            </div>
            
            {/* 🔗 Influencer Link Generator */}
            <div className="mb-4 p-3 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-lg">
              <p className="text-xs text-pink-300 mb-2">🔗 Tracking-Link Generator</p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={influencerName}
                  onChange={(e) => setInfluencerName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  placeholder="Name (z.B. julia)"
                  className="flex-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-sm text-white placeholder-white/30 focus:border-pink-500 focus:outline-none"
                />
                <button
                  onClick={() => {
                    if (influencerName) {
                      setGeneratedLink('https://snapshotpro.ai/?ref=' + influencerName);
                    }
                  }}
                  className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white text-sm rounded font-medium transition-all"
                >
                  Generieren
                </button>
              </div>
              {generatedLink && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={generatedLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-black/50 border border-green-500/50 rounded text-sm text-green-300 font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedLink).then(() => {
                        alert('Link kopiert!');
                      }).catch(() => {
                        // Fallback: Select the input
                        document.querySelector('input[readonly]')?.select();
                        alert('Bitte manuell kopieren (Cmd+C)');
                      });
                    }}
                    className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-sm rounded"
                  >
                    📋
                  </button>
                </div>
              )}
            </div>
            
            {/* Hauptzahl: Gäste jetzt */}
            <p className="text-3xl md:text-4xl font-bold text-orange-400 mb-1">
              {guestStats?.guests_online || 0}
            </p>
            <p className="text-xs text-white/50 mb-4">
              👀 Gäste gerade online
            </p>
            
            {/* Heute Stats */}
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/10 mb-3">
              <div>
                <p className="text-xs text-white/50">Heute Besuche</p>
                <p className="text-lg font-bold text-white">{guestStats?.today?.visits || 0}</p>
              </div>
              <div>
                <p className="text-xs text-white/50">Unique Visitors</p>
                <p className="text-lg font-bold text-white">{guestStats?.today?.unique_visitors || 0}</p>
              </div>
            </div>
            
            {/* Top Sources */}
            {guestStats?.top_sources?.length > 0 && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs text-white/50 mb-2">Top Quellen heute</p>
                <div className="flex flex-wrap gap-2">
                  {guestStats.top_sources.map((s, i) => (
                    <span key={i} className={`px-2 py-1 text-xs rounded ${
                      s.source === 'instagram' ? 'bg-pink-500/20 text-pink-300' :
                      s.source === 'google' ? 'bg-blue-500/20 text-blue-300' :
                      s.source === 'direct' ? 'bg-gray-500/20 text-gray-300' :
                      'bg-white/10 text-white/70'
                    }`}>
                      {s.source === 'instagram' ? '📸' : s.source === 'google' ? '🔍' : '🔗'} {s.source}: {s.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Daily Chart (simplified) */}
            {guestStats?.daily?.length > 0 && (
              <div className="pt-3 mt-3 border-t border-white/10">
                <p className="text-xs text-white/50 mb-2">Letzte 7 Tage</p>
                <div className="flex items-end gap-1 h-16">
                  {guestStats.daily.slice(0, 7).reverse().map((d, i) => {
                    const maxVisits = Math.max(...guestStats.daily.map(x => x.unique || 1));
                    const height = ((d.unique || 0) / maxVisits) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-orange-500/50 rounded-t"
                          style={{ height: `${Math.max(height, 5)}%` }}
                          title={`${d.date}: ${d.unique} Besucher`}
                        />
                        <span className="text-[8px] text-white/30 mt-1">{d.date?.slice(8)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Traffic Sources - Combined & Sorted by Clicks */}
            {(guestStats?.utm_campaigns?.length > 0 || guestStats?.ref_codes?.length > 0) && (
              <div className="pt-3 mt-3 border-t border-white/10">
                <p className="text-xs text-white/50 mb-2">🎯 Traffic Quellen (sortiert nach Klicks)</p>
                <div className="space-y-1">
                  {[
                    ...(guestStats?.ref_codes?.map(r => ({ type: 'ref', label: `?ref=${r.ref}`, count: r.count, color: 'text-pink-300', icon: '💎' })) || []),
                    ...(guestStats?.utm_campaigns?.map(u => ({ type: 'utm', label: `${u.source}${u.campaign !== '-' ? ` (${u.campaign})` : ''}`, count: u.count, color: 'text-yellow-300', icon: '⭐' })) || [])
                  ]
                    .sort((a, b) => b.count - a.count)
                    .map((item, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className={item.color}>{item.icon} {item.label}</span>
                        <span className="text-white font-bold">{item.count}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {/* Raw Domains (Pro!) */}
            {guestStats?.top_domains?.length > 0 && (
              <div className="pt-3 mt-3 border-t border-white/10">
                <p className="text-xs text-white/50 mb-2">🔗 Top Referrer Domains</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {guestStats.top_domains.slice(0, 10).map((d, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-white/70 truncate max-w-[70%]">{d.domain}</span>
                      <span className="text-white/90">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Page Funnel */}
            {guestStats?.pages?.length > 0 && (
              <div className="pt-3 mt-3 border-t border-white/10">
                <p className="text-xs text-white/50 mb-2">📍 Seiten besucht</p>
                <div className="space-y-1">
                  {guestStats.pages.slice(0, 5).map((p, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-white/70">{p.page}</span>
                      <span className="text-white/90">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Top Countries */}
            {guestStats?.top_countries?.length > 0 && (
              <div className="pt-3 mt-3 border-t border-white/10">
                <p className="text-xs text-white/50 mb-2">🌍 Top Länder</p>
                <div className="flex flex-wrap gap-2">
                  {guestStats.top_countries.map((c, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded">
                      {c.country}: {c.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Block 1a2: Guest Visitors Detail-Liste */}
          <div className="p-4 md:p-6 bg-black border-2 border-orange-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 md:w-6 md:h-6 text-orange-400" />
                <h3 className="text-sm md:text-base font-bold text-white/80">Guest Besucher (ohne Login)</h3>
              </div>
              {/* Period Filter */}
              <div className="flex gap-1">
                {['now', 'today', 'week'].map(p => (
                  <button
                    key={p}
                    onClick={() => setGuestPeriod(p)}
                    className={`px-2 py-1 text-xs rounded ${
                      guestPeriod === p
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    {p === 'now' ? 'Live' : p === 'today' ? 'Heute' : '7 Tage'}
                  </button>
                ))}
              </div>
            </div>

            {guestVisitors?.total > 0 && (
              <p className="text-xs text-white/40 mb-2">
                {guestVisitors.total} unique IPs im Zeitraum
              </p>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-white/50 border-b border-white/10">
                    <th className="text-left py-2 px-1">IP</th>
                    <th className="text-left py-2 px-1">Ort</th>
                    <th className="text-left py-2 px-1 hidden md:table-cell">ISP</th>
                    <th className="text-left py-2 px-1">Seite</th>
                    <th className="text-left py-2 px-1">Quelle</th>
                    <th className="text-left py-2 px-1 hidden md:table-cell">Besuche</th>
                    <th className="text-left py-2 px-1 hidden md:table-cell">Zuletzt</th>
                    <th className="text-left py-2 px-1">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {guestVisitors?.visitors?.map((v, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 px-1 text-orange-300 font-mono text-[10px]">{v.ip || '-'}</td>
                      <td className="py-2 px-1 text-white/70">
                        {v.city !== '-' || v.country !== '-'
                          ? `${v.city !== '-' ? v.city : ''}${v.city !== '-' && v.country !== '-' ? ', ' : ''}${v.country !== '-' ? v.country : ''}`
                          : '-'}
                      </td>
                      <td className="py-2 px-1 text-white/50 hidden md:table-cell truncate max-w-[120px]">{v.isp !== '-' ? v.isp : '-'}</td>
                      <td className="py-2 px-1 text-white/70">{v.last_page || '-'}</td>
                      <td className="py-2 px-1">
                        <span className={`${
                          v.source === 'direct' ? 'text-gray-400' :
                          v.source?.includes('instagram') ? 'text-pink-300' :
                          v.source?.includes('google') ? 'text-blue-300' :
                          'text-yellow-300'
                        }`}>
                          {v.source || 'direct'}
                        </span>
                      </td>
                      <td className="py-2 px-1 text-white/60 hidden md:table-cell">{v.visits}</td>
                      <td className="py-2 px-1 text-white/50 hidden md:table-cell">
                        {v.last_seen ? new Date(v.last_seen).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="py-2 px-1">
                        {v.ip && v.ip !== '-' && v.ip !== 'unknown' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`IP ${v.ip} wirklich sperren?`)) {
                                banIP(v.ip, `Manuell gesperrt - Guest`);
                              }
                            }}
                            disabled={banningIP === v.ip}
                            className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded transition-all disabled:opacity-50"
                            title="IP sperren"
                          >
                            {banningIP === v.ip ? '...' : '🚫'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!guestVisitors?.visitors || guestVisitors.visitors.length === 0) && (
                    <tr>
                      <td colSpan="8" className="py-4 text-center text-white/30">
                        Keine Gast-Besucher im gewählten Zeitraum
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Block 1b: Live Traffic Monitor - Detailierte User-Liste */}
          <div className="p-4 md:p-6 bg-black border-2 border-blue-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
                <h3 className="text-sm md:text-base font-bold text-white/80">Live Traffic Monitor (eingeloggt)</h3>
              </div>
              {/* Period Filter */}
              <div className="flex gap-1">
                {['now', 'today', 'week'].map(p => (
                  <button
                    key={p}
                    onClick={() => setDetailPeriod(p)}
                    className={`px-2 py-1 text-xs rounded ${
                      detailPeriod === p 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    {p === 'now' ? 'Live' : p === 'today' ? 'Heute' : '7 Tage'}
                  </button>
                ))}
              </div>
            </div>
            
            {/* User-Tabelle */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-white/50 border-b border-white/10">
                    <th className="text-left py-2 px-1">User</th>
                    <th className="text-left py-2 px-1 hidden md:table-cell">IP</th>
                    <th className="text-left py-2 px-1">Ort</th>
                    <th className="text-left py-2 px-1">Geraet/Browser</th>
                    <th className="text-left py-2 px-1 hidden md:table-cell">Zuletzt</th>
                    <th className="text-left py-2 px-1">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {activeUsersDetailed?.users?.map((user, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 px-1 text-white/90">
                        <div>{user.name || '-'}</div>
                        <div className="text-white/50 text-[10px]">{user.email}</div>
                      </td>
                      <td className="py-2 px-1 text-white/50 font-mono hidden md:table-cell">{user.ip || '-'}</td>
                      <td className="py-2 px-1 text-white/70">{user.location || '-'}</td>
                      <td className="py-2 px-1 text-white/70">
                        <div>{user.device || '-'}</div>
                        {user.browser && <div className="text-white/40 text-[10px]">{user.browser}</div>}
                      </td>
                      <td className="py-2 px-1 text-white/50 hidden md:table-cell">
                        {user.last_seen ? new Date(user.last_seen).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="py-2 px-1">
                        {user.ip && user.ip !== '-' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`IP ${user.ip} wirklich sperren?`)) {
                                banIP(user.ip, `Manuell gesperrt - User: ${user.email}`);
                              }
                            }}
                            disabled={banningIP === user.ip}
                            className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded transition-all disabled:opacity-50"
                            title="IP sperren"
                          >
                            {banningIP === user.ip ? '...' : '🚫'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!activeUsersDetailed?.users || activeUsersDetailed.users.length === 0) && (
                    <tr>
                      <td colSpan="6" className="py-4 text-center text-white/30">
                        Keine aktiven User im gewählten Zeitraum
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {activeUsersDetailed?.count > 5000 && (
              <p className="text-xs text-white/40 mt-2 text-center">
                +{activeUsersDetailed.count - 50} weitere User
              </p>
            )}
          </div>

          {/* Block 1c: Blocked IPs */}
          {bannedIPs?.count > 0 && (
            <div className="p-4 md:p-6 bg-black border-2 border-red-500/30 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-red-400" />
                  <h3 className="text-sm md:text-base font-bold text-white/80">
                    Gesperrte IPs ({bannedIPs.count})
                  </h3>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-white/50 border-b border-white/10">
                      <th className="text-left py-2 px-1">IP</th>
                      <th className="text-left py-2 px-1">Grund</th>
                      <th className="text-left py-2 px-1 hidden md:table-cell">Gesperrt am</th>
                      <th className="text-left py-2 px-1 hidden md:table-cell">Läuft ab</th>
                      <th className="text-left py-2 px-1">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bannedIPs?.banned_ips?.map((ban, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 px-1 text-red-400 font-mono">{ban.ip}</td>
                        <td className="py-2 px-1 text-white/70 max-w-32 truncate">{ban.reason}</td>
                        <td className="py-2 px-1 text-white/50 hidden md:table-cell">
                          {ban.banned_at ? new Date(ban.banned_at).toLocaleDateString('de-DE') : '-'}
                        </td>
                        <td className="py-2 px-1 text-white/50 hidden md:table-cell">
                          {ban.is_permanent ? '♾️ Permanent' : new Date(ban.expires_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="py-2 px-1">
                          <button
                            onClick={() => {
                              if (window.confirm(`IP ${ban.ip} wirklich entsperren?`)) {
                                unbanIP(ban.ip);
                              }
                            }}
                            className="px-2 py-1 text-xs bg-green-500/20 hover:bg-green-500/40 text-green-400 rounded transition-all"
                            title="IP entsperren"
                          >
                            ✅
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Block 2: Umsatz (Einnahmen) - OBEN */}
          <div className="p-4 md:p-6 bg-black border-2 border-[#10B981]/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Umsatz (Einnahmen)</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-[#10B981] mb-1">
              €{kpis?.revenue.brutto.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/50 break-words">
              Netto: €{kpis?.revenue.netto.toFixed(2) || '0.00'} · MwSt: €{kpis?.revenue.mwst.toFixed(2) || '0.00'} · {kpis?.transactions_count || 0} Transaktionen
            </p>
          </div>

          {/* Block 3: Marge (Gewinn/Verlust) */}
          <div className={`p-4 md:p-6 bg-black border-2 rounded-xl ${
            (kpis?.margin || 0) >= 0 
              ? 'border-[#10B981]/30' 
              : 'border-red-500/30'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className={`w-5 h-5 md:w-6 md:h-6 ${
                (kpis?.margin || 0) >= 0 ? 'text-[#10B981]' : 'text-red-500'
              }`} />
              <h3 className="text-sm md:text-base font-bold text-white/80">Marge (Gewinn/Verlust)</h3>
            </div>
            <p className={`text-3xl md:text-4xl font-bold mb-1 ${
              (kpis?.margin || 0) >= 0 ? 'text-[#10B981]' : 'text-red-500'
            }`}>
              €{kpis?.margin.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/50">
              {kpis?.transactions_count || 0} Transaktionen
            </p>
          </div>

          {/* Block 4: USt-Zahllast (Finanzamt) */}
          <div className="p-4 md:p-6 bg-black border-2 border-orange-500/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
              <h3 className="text-sm md:text-base font-bold text-white/80">USt-Zahllast</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-1">
              €{kpis?.revenue.mwst.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/50">
              An Finanzamt (19% MwSt)
            </p>
          </div>

          {/* MITTLERE SEKTION - Aktivität */}
          
          {/* Block 5: Neue User */}
          <div className="p-4 md:p-6 bg-black border-2 border-white/20 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Neue User</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-white mb-1">
              {kpis?.users.new || 0}
            </p>
            <p className="text-xs text-white/50">
              Gesamt: {kpis?.users.total || 0} User
            </p>
          </div>

          {/* Block 6: Avatare Detail */}
          <div className="p-4 md:p-6 bg-black border-2 border-white/20 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Camera className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Avatare</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-white mb-1">
              {kpis?.avatars.active || 0}
            </p>
            <p className="text-xs text-white/50">
              Training: {kpis?.avatars.training || 0} · Gesamt: {kpis?.avatars.total || 0}
            </p>
          </div>

          {/* Block 7: Referrals & SNAPs verdient */}
          <div className="p-4 md:p-6 bg-black border-2 border-white/20 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Referrals</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-white mb-1">
              {kpis?.referrals?.active || 0}
            </p>
            <p className="text-xs text-white/50">
              Links aktiv · Gesamt: {kpis?.referrals?.total || 0}
            </p>
          </div>

          {/* UNTERE SEKTION - Kosten & System */}
          
          {/* Block 8: Kosten aufgeteilt */}
          <div className="p-4 md:p-6 bg-black border-2 border-red-500/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Gesamtkosten</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-red-500 mb-1">
              €{kpis?.costs.total?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/50">
              Stripe + Astria + OpenAI
            </p>
          </div>
          
          <div className="p-4 md:p-6 bg-black border-2 border-red-500/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Stripe-Gebühren</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-red-500 mb-1">
              €{kpis?.costs.stripe_fees.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/50">
              Zahlungsabwicklung
            </p>
          </div>

          <div className="p-4 md:p-6 bg-black border-2 border-orange-500/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
              <h3 className="text-sm md:text-base font-bold text-white/80">Astria-Kosten</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-1">
              €{kpis?.costs.astria_total.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/50 break-words">
              Training: €{kpis?.costs.astria_training.toFixed(2) || '0.00'} · Generation: €{kpis?.costs.astria_generation.toFixed(2) || '0.00'}
            </p>
          </div>

          {/* Block 8b: OpenAI-Kosten (NEU!) */}
          <div className="p-4 md:p-6 bg-black border-2 border-blue-500/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.033.062L9.74 19.95a4.5 4.5 0 01-6.14-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
              </svg>
              <h3 className="text-sm md:text-base font-bold text-white/80">OpenAI-Kosten</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-blue-500 mb-1">
              €{kpis?.costs.openai_eur?.toFixed(4) || '0.0000'}
            </p>
            <p className="text-xs text-white/50 break-words">
              ${kpis?.costs.openai_usd?.toFixed(4) || '0.0000'} · {kpis?.costs.openai_calls || 0} API-Calls
            </p>
          </div>

          {/* Block 9: SNAPs im Umlauf */}
          <div className="p-4 md:p-6 bg-black border-2 border-[#10B981]/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">SNAPs im Umlauf</h3>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-[#10B981] mb-1">
              {kpis?.total_snaps || 0}
            </p>
            <p className="text-xs text-white/50">
              Interner Token-Bestand
            </p>
          </div>

        </div>

        {/* Block 10: System-Health */}
        {health && (
          <div className="p-4 md:p-6 bg-black border-2 border-white/20 rounded-xl mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Activity className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
              <h3 className="text-sm md:text-base font-bold text-white/80">System-Health</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                {health.stripe?.status === 'ok' ? (
                  <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-[#10B981] mx-auto mb-1" />
                ) : (
                  <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500 mx-auto mb-1" />
                )}
                <p className="text-xs text-white/60">Stripe</p>
              </div>
              <div className="text-center">
                {health.astria?.status === 'ok' ? (
                  <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-[#10B981] mx-auto mb-1" />
                ) : (
                  <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500 mx-auto mb-1" />
                )}
                <p className="text-xs text-white/60">Astria</p>
              </div>
              <div className="text-center">
                {health.queue?.status === 'ok' ? (
                  <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-[#10B981] mx-auto mb-1" />
                ) : (
                  <XCircle className="w-6 h-6 md:w-8 md:h-8 text-yellow-500 mx-auto mb-1" />
                )}
                <p className="text-xs text-white/60">Queue</p>
                <p className="text-xs text-white/40">{health.queue?.pending_jobs || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Block 11: Quick Actions */}
        <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture mb-8">
          <h3 className="text-lg md:text-xl font-bold mb-4">Quick Actions</h3>
          
          <div className="space-y-3">
            <button
              onClick={() => onViewChange && onViewChange('invoices')}
              data-testid="quick-action-invoices"
              className="w-full p-4 bg-white/5 border-2 border-white/20 rounded-xl hover:border-[#10B981] transition-all flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium flex-1 text-left min-w-0">Rechnungen</span>
              <svg className="w-4 h-4 text-white/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <button
              onClick={() => onViewChange && onViewChange('tax-overview')}
              data-testid="quick-action-tax-overview"
              className="w-full p-4 bg-white/5 border-2 border-white/20 rounded-xl hover:border-[#10B981] transition-all flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium flex-1 text-left min-w-0">Steuerübersicht</span>
              <svg className="w-4 h-4 text-white/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <button
              onClick={handleExport}
              data-testid="quick-action-csv-export"
              className="w-full p-4 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all flex items-center gap-3 font-bold"
            >
              <Download className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm flex-1 text-left min-w-0">CSV Export</span>
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Charts Section */}
        {chartsData && (
          <div ref={chartsRef} className="space-y-8">
            
            {/* Charts Header - Mobile Optimized */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-[#10B981]" />
                <h2 className="text-xl md:text-2xl font-bold">Analytics & Charts</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleExportChartsCSV}
                  data-testid="export-charts-csv-button"
                  className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 bg-black border-2 border-white/30 rounded-xl text-white hover:border-[#10B981] transition-all flex items-center justify-center gap-2 text-sm touch-manipulation"
                >
                  <FileDown className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={handleExportChartsPNG}
                  data-testid="export-charts-png-button"
                  className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 bg-black border-2 border-white/30 rounded-xl text-white hover:border-[#10B981] transition-all flex items-center justify-center gap-2 text-sm touch-manipulation"
                >
                  <ImageDown className="w-4 h-4" />
                  <span>PNG</span>
                </button>
              </div>
            </div>

            {/* Chart 1: Revenue vs Costs - Mobile Optimized */}
            <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture" data-testid="chart-revenue-costs">
              <h3 className="text-base md:text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#10B981]" />
                Umsatz vs. Astria-Kosten
              </h3>
              
              {chartsData.revenue_vs_costs.length > 0 ? (
                <ResponsiveContainer width="100%" height={300} className="md:h-[350px]">
                  <ComposedChart data={chartsData.revenue_vs_costs}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#ffffff60" 
                      tick={{ fill: '#ffffff80', fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      stroke="#ffffff60" 
                      tick={{ fill: '#ffffff80', fontSize: 10 }}
                      label={{ value: 'EUR (€)', angle: -90, position: 'insideLeft', fill: '#ffffff60', fontSize: 11 }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#000000',
                        border: '2px solid #10B981',
                        borderRadius: '12px',
                        padding: '8px 12px',
                        color: '#ffffff',
                        maxWidth: '280px'
                      }}
                      labelStyle={{ color: '#10B981', fontWeight: 'bold', marginBottom: '6px', fontSize: '12px' }}
                      itemStyle={{ color: '#ffffff', fontSize: '11px', padding: '2px 0' }}
                      formatter={(value, name) => {
                        if (name === 'Umsatz (€)') {
                          const dataPoint = chartsData.revenue_vs_costs.find(d => d.revenue === value);
                          if (dataPoint) {
                            const brutto = dataPoint.revenue;
                            const netto = brutto / 1.19;
                            const mwst = brutto - netto;
                            const stripeFee = (brutto * 1.5 / 100) + 0.25;
                            const margin = brutto - stripeFee - dataPoint.astria_costs;
                            
                            return [`€${brutto.toFixed(2)}`, `Netto: €${netto.toFixed(2)} | Marge: €${margin.toFixed(2)}`];
                          }
                        }
                        return `€${Number(value).toFixed(2)}`;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="circle"
                    />
                    <Bar 
                      dataKey="revenue" 
                      fill="#10B981" 
                      name="Umsatz (€)" 
                      radius={[8, 8, 0, 0]}
                      maxBarSize={60}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="astria_costs" 
                      stroke="#f97316" 
                      strokeWidth={3}
                      name="Astria-Kosten (€)"
                      dot={{ fill: '#f97316', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-white/40">
                  <p>Keine Daten für den ausgewählten Zeitraum</p>
                </div>
              )}
            </div>

            {/* Charts Grid: Purchases + Generations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Chart 2: Purchases per Day */}
              <div className="p-6 bg-black border-2 border-white/10 rounded-xl grain-texture" data-testid="chart-purchases">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#10B981]" />
                  Käufe pro Tag
                </h3>
                
                {chartsData.purchases_per_day.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartsData.purchases_per_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#ffffff60" 
                        tick={{ fill: '#ffffff80', fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis 
                        stroke="#ffffff60" 
                        tick={{ fill: '#ffffff80', fontSize: 11 }}
                        allowDecimals={false}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#000000',
                          border: '2px solid #10B981',
                          borderRadius: '12px',
                          padding: '10px'
                        }}
                        labelStyle={{ color: '#10B981', fontWeight: 'bold' }}
                        cursor={{ fill: '#ffffff10' }}
                      />
                      <Bar 
                        dataKey="purchases" 
                        fill="#10B981" 
                        name="Käufe" 
                        radius={[6, 6, 0, 0]}
                        maxBarSize={50}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-white/40">
                    <p>Keine Käufe im Zeitraum</p>
                  </div>
                )}
              </div>

              {/* Chart 3: Generations per Day */}
              <div className="p-6 bg-black border-2 border-white/10 rounded-xl grain-texture" data-testid="chart-generations">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#10B981]" />
                  Generierungen pro Tag
                </h3>
                
                {chartsData.generations_per_day.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartsData.generations_per_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#ffffff60" 
                        tick={{ fill: '#ffffff80', fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis 
                        stroke="#ffffff60" 
                        tick={{ fill: '#ffffff80', fontSize: 11 }}
                        allowDecimals={false}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#000000',
                          border: '2px solid #10B981',
                          borderRadius: '12px',
                          padding: '10px'
                        }}
                        labelStyle={{ color: '#10B981', fontWeight: 'bold' }}
                        cursor={{ fill: '#ffffff10' }}
                      />
                      <Bar 
                        dataKey="generations" 
                        fill="#059669" 
                        name="Generierungen" 
                        radius={[6, 6, 0, 0]}
                        maxBarSize={50}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-white/40">
                    <p>Keine Generierungen im Zeitraum</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

        {/* Info */}
        <div className="text-center mt-8">
          <p className="text-white/50 text-sm">
            SNAPSHOT Admin Panel · Phase 4a Steuerberater-Modul integriert
          </p>
        </div>

      </div>
    </div>
  );
}
